/* eslint-disable max-len */
/** @prettier */
import classNames from 'classnames';
import { addDays, format, isBefore, parse } from 'date-fns';
import Cookie from 'js-cookie';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import Button from '@xanadu/ui-shared/components/widgets/Button';
import { MATCHBOOK_UI } from '@xanadu/ui-shared/constants';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';

import { getClientString } from '../../utils';

import ArrowUpIcon from '../../../../images/icons/misc/Arrow-Up.svg';
import NewReleaseIcon from '../../../../images/icons/misc/newrelease.svg';

import Rating from '../Rating';

import styles from './ReleaseNotes.module.scss';

const RATING_ENABLED = true;

const DATE = '15-9-2026';
const RELEASE_DATE = parse(DATE, 'dd-MM-yyyy', new Date());
const RELEASE_NOTES_DEADLINE = addDays(RELEASE_DATE, 8);
const isBeforeDeadline = isBefore(new Date(RELEASE_DATE), new Date(RELEASE_NOTES_DEADLINE));

const isInitialRelease = true;

const ReleaseNotes = ({ setIsActive, isActive, position = 'top' }) => {
  const {
    clientName,
    clientSite,
    featuresFlags: {
      switchSiteLayout = false
    } = {}
  } = useReduxBridge() || {};
  const [rated, setRated] = useState(false);
  const [disappeared, setDisappeared] = useState(false);
  const { t } = useImprovedTranslation();

  const releaseDate = format(RELEASE_DATE, 'dd-MM-yyyy');

  // TODO: add interaction with data layer
  let previousRating;
  try {
    previousRating = localStorage.getItem('mb-user-rating');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Error accessing the local storage');
    // set to release date
    previousRating = releaseDate;
  }

  const isFeedbackAlreadySubmitted = previousRating === releaseDate;

  const dismissedFeedback = () => {
    setIsActive(!isActive);
    setTimeout(() => {
      setDisappeared(true);
    }, 500);
  };

  const switchToOldVersion = () => {
    Cookie.set('mb-ui-variant', MATCHBOOK_UI.CLASSIC, {
      path: '/',
      expires: 365,
    });
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  useEffect(() => {
    if (isFeedbackAlreadySubmitted) {
      setDisappeared(true);
    }
  }, []);

  useEffect(() => {
    if (rated && !switchSiteLayout) {
      setTimeout(() => {
        setDisappeared(true);
      }, 2000);
    }
  }, [rated, switchSiteLayout]);

  const clientString = getClientString(clientName, clientSite);

  const title = switchSiteLayout ?
    `New look ${clientString}.${clientSite}` :
    t('clientName Predictions is here!', { clientName: clientString });

  let collapsedHeaderTitle = t('Let us know what you think');
  if (position === 'bottom') {
    collapsedHeaderTitle = title;
  } else if (!isInitialRelease) {
    collapsedHeaderTitle = (
      <Trans i18nKey="What's new on" clientString={clientString}>
        What&#39;s new on {{ clientString }}?
      </Trans>
    );
  }

  const text = switchSiteLayout ? (
    <React.Fragment>
      <Trans i18nKey="We know change can be hard, but we'd love to hear your feedback on the new look of" clientString={clientString} clientSite={clientSite}>
        We know change can be hard, but we&#39;d love to hear your feedback on the new look of {{ clientString }}.{{ clientSite }}
      </Trans>
      <br />
      <br />
      {t('Do you love it? Do you hate it? Tell us your thoughts below.')}
    </React.Fragment>
  ) : (
    <React.Fragment>
      {t('Choose the experience that best suits how you play, or switch seamlessly between the two.')}
      <br />
      <br />
      <b>{`${clientString} Predictions `}</b>
      {t('has a simplified Yes/No betting experience and fan favourite trending markets where your opinion matters!')}
      <br />
      <br />
      {t('And now')} <b>{clientString} {t('Exchange')}</b>
      {' '}
      {t("has the full range of sports and markets you've come to expect; multiples, bet-builders and cash-out.")}
      <br />
      <br />
      {t('As always, help us improve by leaving your feedback below.')}
    </React.Fragment>
  );

  const mainContainerClass = classNames({
    [styles.mainContainer]: true,
    [styles[`mainContainer--${position}`]]: true,
    [styles['mainContainer--expanded']]: isActive,
  });

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--expanded']]: isActive,
  });

  const iconContainerClass = classNames({
    [styles.iconContainer]: true,
    [styles[`iconContainer--${position}`]]: true,
    [styles['iconContainer--collapsed']]: !isActive,
    [styles['iconContainer--expanded']]: isActive,
  });

  const returnToOldButtonClass = classNames({
    [styles.button]: true,
    [styles.returnToOldButton]: true,
  });

  if (!isBeforeDeadline || disappeared) {
    return null;
  }


  if (rated || isFeedbackAlreadySubmitted) {
    return (
      <div className={mainContainerClass}>
        <button className={iconContainerClass} onClick={() => setIsActive(!isActive)}>
          <ArrowUpIcon />
        </button>
        <div role="button" className={mainClass} onClick={() => setIsActive(!isActive)}>
          <div className={styles.data}>
            <NewReleaseIcon className={styles.releaseIcon} />
            {title}
          </div>
        </div>
        {isActive && (
          <div className={styles.contentContainer}>
            <div className={styles.content}>
              <div>{t('Thank you for your feedback.')}</div>
              <br />
              <div>{t('Your feedback is always appreciated and helps us to build a better site.')}</div>
              <br />
              {switchSiteLayout && (
                <React.Fragment>
                  <div>
                    <Trans i18nKey="You may now choose to continue using the new layout or return to the old clientName.clientSite site." clientName={clientName} clientSite={clientSite}>
                      You may now choose to continue using the new layout or return to the old {{ clientName }}.{{ clientSite }} site.
                    </Trans>
                  </div>
                  <br />
                  <div>{t('You can also return to the old layout at any time via your account settings.')}</div>
                  <div className={styles.buttonContainer}>
                    <Button
                      sizeType="wider"
                      buttonType="flat-black"
                      className={returnToOldButtonClass}
                      onClick={switchToOldVersion}
                    >
                      {t('Return to old')}
                    </Button>
                    <Button
                      sizeType="wider"
                      buttonType="alternative"
                      className={styles.button}
                      onClick={dismissedFeedback}
                    >
                      {t('Stay on new')}
                    </Button>
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={mainContainerClass}>
      <button className={iconContainerClass} onClick={() => setIsActive(!isActive)}>
        <ArrowUpIcon />
      </button>
      <div role="button" className={mainClass} onClick={() => setIsActive(!isActive)}>
        <div className={styles.data}>
          <NewReleaseIcon className={styles.releaseIcon} />
          <span className={styles.title}>
            {collapsedHeaderTitle}
          </span>
        </div>
      </div>
      {isActive && (
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            <div>
              <b>{title}</b>
            </div>
            <div>{text}</div>
            {RATING_ENABLED && !isFeedbackAlreadySubmitted && (
              <Rating
                totalStars={5}
                releaseDate={releaseDate}
                releaseDescription={`${clientString} ${t('Release')}`}
                setRated={setRated}
                clientName={clientName}
                clientSite={clientSite}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ReleaseNotes.propTypes = {
  isActive: PropTypes.bool.isRequired,
  setIsActive: PropTypes.func.isRequired,
  position: PropTypes.oneOf(['top', 'bottom']),
};

export default ReleaseNotes;
